class StickyAddToCart extends HTMLElement {
  constructor() {
    super();

    this.form = null;
    this.footer = null;
    this.content = null;
    // `anchorInView` = le bouton d'ajout (position in-flow de ce host) est-il
    // visible à l'écran ? On part de `true` pour ne pas flasher la barre avant
    // la première mesure.
    this.anchorInView = true;
    this.footerInView = false;
    this.anchorObserver = null;
    this.footerObserver = null;
    this.contentResizeObserver = null;
    this.onViewportChange = null;
    this.ticking = false;
  }

  connectedCallback() {
    // Repli sur une recherche globale : sur certains rendus (quick-add, galerie
    // re-rendue au changement de variante) l'élément n'est plus un descendant
    // du <product-form> au moment où il est connecté. Le form ne sert ici qu'à
    // confirmer qu'on est bien dans un contexte produit.
    this.form = this.closest('[data-ref="product-form"]') || document.querySelector('[data-ref="product-form"]');
    this.footer = document.querySelector('[data-ref="footer"]') || document.querySelector('footer');
    this.content = this.querySelector('.sticky-add-to-cart__content');

    if (!this.form || !this.content) return;

    // Réserve l'espace du sticky dans le flux pour éviter le changement de
    // hauteur du document quand le contenu passe en position: fixed. Sans cela,
    // le contenu sort du flux → la page raccourcit → le host remonte/descend
    // dans le viewport → le sticky se désactive → boucle infinie de clignotement.
    requestAnimationFrame(() => {
      this.#reserveSpace();
      this.#initObservers();
      this.#updateFromRects();
    });

    // Recalcule la hauteur réservée quand le contenu change de taille SANS
    // provoquer de scroll/resize fenêtre (police web chargée après coup, image
    // paresseuse, texte de variante qui se ré-enroule) — et ferme le trou
    // « offsetHeight = 0 au connect » (quick-add masqué) : le RO se déclenche
    // une fois à l'observe, puis à l'ouverture du tiroir. Pas de boucle : écrire
    // la min-height du host ne change pas la hauteur du contenu, et #reserveSpace
    // sort tôt tant que la barre est active/en fermeture.
    if ('ResizeObserver' in window) {
      this.contentResizeObserver = new ResizeObserver(() => this.#reserveSpace());
      this.contentResizeObserver.observe(this.content);
    }

    // Filet de sécurité mobile : si les IntersectionObserver ne rendent jamais
    // la main (observation posée avant la mise en page, barre d'URL rétractable
    // sur iOS, ancêtre transformé), on recalcule à chaque scroll / resize. Groupé
    // dans un requestAnimationFrame pour au plus une mesure par frame (évite le
    // jank sur mobile bas de gamme : sans ça, 2 reflows par événement de scroll).
    this.onViewportChange = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.#reserveSpace();
        this.#updateFromRects();
      });
    };
    window.addEventListener('scroll', this.onViewportChange, { passive: true });
    window.addEventListener('resize', this.onViewportChange, { passive: true });
    window.addEventListener('orientationchange', this.onViewportChange, { passive: true });
  }

  disconnectedCallback() {
    this.anchorObserver?.disconnect();
    this.footerObserver?.disconnect();
    this.contentResizeObserver?.disconnect();
    clearTimeout(this.closingTimeout);

    if (this.onViewportChange) {
      window.removeEventListener('scroll', this.onViewportChange);
      window.removeEventListener('resize', this.onViewportChange);
      window.removeEventListener('orientationchange', this.onViewportChange);
    }
  }

  // La hauteur réservée n'est mesurable que tant que le contenu est dans le
  // flux : une fois passé en fixed, offsetHeight vaudrait 0 et l'espace
  // réservé disparaîtrait (d'où le clignotement décrit plus haut). On mesure
  // le CONTENU (content.offsetHeight), pas le host : utiliser this.offsetHeight
  // ferait cliqueter la réserve (jamais plus courte qu'une min-height déjà
  // posée) et laisserait un vide sous le bouton si une variante plus courte
  // se rendait.
  #reserveSpace() {
    if (this.dataset.active === 'true' || this.dataset.closing === 'true') return;

    const height = this.content?.offsetHeight || this.offsetHeight;
    if (height > 0) this.style.minHeight = `${height}px`;
  }

  #initObservers() {
    // On observe le HOST <sticky-add-to-cart> lui-même, PAS le <product-form>
    // entier. Le host reste dans le flux à la position naturelle du bouton
    // d'ajout (grâce à la hauteur réservée ci-dessus), même quand son contenu
    // passe en position: fixed — c'est donc une ancre stable qui suit
    // précisément la visibilité du bouton.
    //   • Observer tout le form ratait le cas « haut de page » : le sélecteur
    //     de pointure (en haut du form) restait visible alors que le bouton
    //     (en bas du form) était déjà sous le pli → la barre ne s'activait pas.
    //   • Observer le bouton lui-même créerait une boucle : il bouge en passant
    //     en fixed. Le host, lui, ne bouge jamais → zéro clignotement.
    // Résultat : la barre apparaît dès que le bouton n'est pas à l'écran, qu'on
    // soit trop bas OU trop haut sur la page.
    this.anchorObserver = new IntersectionObserver(([entry]) => {
      this.anchorInView = entry.isIntersecting;
      this.#updateDisplay();
    });

    this.anchorObserver.observe(this);

    if (!this.footer) return;

    // Le footer garde la priorité : on ne recouvre pas le bas de page.
    this.footerObserver = new IntersectionObserver(([entry]) => {
      this.footerInView = entry.isIntersecting;
      this.#updateDisplay();
    });

    this.footerObserver.observe(this.footer);
  }

  #updateFromRects() {
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const anchorRect = this.getBoundingClientRect();
    this.anchorInView = anchorRect.bottom > 0 && anchorRect.top < viewportHeight;

    if (this.footer) {
      const footerRect = this.footer.getBoundingClientRect();
      this.footerInView = footerRect.bottom > 0 && footerRect.top < viewportHeight;
    }

    this.#updateDisplay();
  }

  #updateDisplay() {
    const shouldBeActive = !this.anchorInView && !this.footerInView;
    const isActive = this.dataset.active === 'true';

    if (shouldBeActive === isActive) return;

    clearTimeout(this.closingTimeout);

    if (shouldBeActive) {
      delete this.dataset.closing;
      this.dataset.active = 'true';
      this.content.classList.add('color-scheme-1');
      return;
    }

    this.dataset.active = 'false';
    this.dataset.closing = 'true';

    this.closingTimeout = setTimeout(() => {
      delete this.dataset.closing;
      this.content.classList.remove('color-scheme-1');
    }, 300);
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
